import React, { useState } from "react";
import classnames from "classnames";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { parse } from "date-fns";
import MuiTable from "@mui/material/Table";
import { TableStyle } from "./table.styles";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import { Collapse, IconButton, TableFooter, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";

import { UpwardIcon, DownwardIcon } from "../../utils/svg-components";

function parseToUnix(dateString, format = "MM/dd/yyyy") {
  const parsedDate = parse(dateString, format, new Date());
  return parsedDate.getTime(); // Unix time in milliseconds
}

const Table = (props) => {
  const {
    data,
    columns,
    className,
    isLoading,
    onRowClick,
    isExpandable,
    onExpandRow,
    defaultExpandedRows,
    noDataMessage,
    customNoData,
    hasFooter = false,
    stickyHeader = false,
    rowBackground = "#efedfd",
    useUniformBackground = false,
    rowHover = false,
    uniqueKey = "",
  } = props;

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    const sortColumn = columns?.find((col) => col.dataKey === sortConfig.key);
    const getSortValue = (row) =>
      sortColumn?.sortAccessor ? sortColumn.sortAccessor(row) : row[sortConfig.key];

    const sorted = [...data].sort((a, b) => {
      const aValue = getSortValue(a) || "";
      const bValue = getSortValue(b) || "";

      const aDate = parseToUnix(aValue);
      const bDate = parseToUnix(bValue);

      const isADate = !isNaN(aDate);
      const isBDate = !isNaN(bDate);

      if (isADate && isBDate) {
        return (aDate - bDate) * (sortConfig.direction === "asc" ? 1 : -1);
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return (
          aValue.localeCompare(bValue, undefined, { numeric: true }) *
          (sortConfig.direction === "asc" ? 1 : -1)
        );
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig, columns]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? <UpwardIcon /> : <DownwardIcon />;
    } else {
      return <DownwardIcon style={{ opacity: 0.5 }} />;
    }
  };

  return (
    <TableStyle>
      {isLoading ? (
        <p></p>
      ) : (
        <>
          <Box sx={{ overflowX: "auto" }}>
            <TableContainer className={className} component={Paper}>
              <MuiTable stickyHeader={stickyHeader}>
                <TableHead>
                  <TableRow>
                    {isExpandable && <TableCell width="1%"> </TableCell>}
                    {columns?.map((column, colIndex) => (
                      <TableCell
                        className={classnames({
                          "status-column":
                            column?.dataKey === "status" && !colIndex,
                        })}
                        key={column.dataKey || colIndex}
                        align={column.align}
                        width={column.width}
                        sx={{ minWidth: column.minWidth }}
                        onClick={() =>
                          column.sortable &&
                          column.dataKey &&
                          requestSort(column.dataKey)
                        }
                        style={{
                          cursor: column.sortable ? "pointer" : "default",
                        }}
                      >
                        <span className={classnames({ "table-title": true })}>
                          <Typography
                            variant={"body2"}
                            color="textTertiary"
                            sx={{ fontWeight: "bold", width: "100%" }}
                          >
                            {column.name}
                          </Typography>
                          {column.sortable &&
                            column.dataKey &&
                            getSortIcon(column.dataKey)}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {!!sortedData?.length && (
                  <TableBody aria-label="body" id="table-body">
                    {sortedData?.map((rowData, index) => (
                      <>
                        <TableRow
                          key={uniqueKey ? rowData[uniqueKey] : rowData.id}
                          id={rowData.tableRowId}
                          role="row"
                          className={classnames(rowData?.className)}
                          onClick={() => {
                            onRowClick && onRowClick(rowData);
                          }}
                          style={{
                            cursor: onRowClick ? "pointer" : "auto",
                            backgroundColor: useUniformBackground
                              ? rowBackground
                              : index % 2 === 0
                              ? "inherit"
                              : rowBackground,
                            transition: "background-color 0.2s ease", // Smooth hover transition
                          }}
                          onMouseEnter={(e) => {
                            if (rowHover)
                              e.currentTarget.style.backgroundColor = "#efedfd"; // Hover color
                          }}
                          onMouseLeave={(e) => {
                            if (rowHover)
                              e.currentTarget.style.backgroundColor =
                                useUniformBackground
                                  ? rowBackground
                                  : index % 2 === 0
                                  ? "inherit"
                                  : rowBackground; // Reset background color
                          }}
                        >
                          {isExpandable && defaultExpandedRows && (
                            <TableCell
                              padding="checkbox"
                              className={classnames({
                                "selected-row": defaultExpandedRows[index],
                              })}
                            >
                              <IconButton
                                disabled={rowData.isExpandableDisable}
                                onClick={() => {
                                  if (onExpandRow) onExpandRow(rowData);
                                }}
                                id={`expandable-btn`}
                              >
                                {defaultExpandedRows[index] ? (
                                  <ArrowDropDownIcon />
                                ) : (
                                  <ArrowRightIcon />
                                )}
                              </IconButton>
                            </TableCell>
                          )}
                          {columns.map((column, colIndex) => (
                            <TableCell
                              className={classnames({
                                "status-column":
                                  column?.dataKey === "status" && !colIndex,
                              })}
                              data-testid={`${rowData.id}-${
                                column.dataKey || colIndex
                              }`}
                              width={column?.width}
                              id={`${rowData.tableRowId}-${
                                column.dataKey || colIndex
                              }`}
                              key={`${rowData.id}-${
                                column.dataKey || colIndex
                              }`}
                              sx={{ minWidth: column.minWidth }}
                            >
                              {column?.render ? (
                                <Typography component="div" variant={"body1"}>
                                  {column.render(
                                    column.dataKey
                                      ? rowData[column.dataKey]
                                      : undefined,
                                    rowData,
                                    index
                                  )}
                                </Typography>
                              ) : (
                                <Typography variant={"body1"}>
                                  {column.dataKey
                                    ? rowData[column.dataKey]
                                    : "-"}
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        {defaultExpandedRows && defaultExpandedRows[index] && (
                          <TableRow key={`expanded_row_${rowData?.id}`}>
                            <TableCell
                              className="expanded-row-cell"
                              colSpan={columns.length + 1}
                            >
                              <Collapse in={defaultExpandedRows[index]}>
                                {rowData.expandableContent}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                )}
                {hasFooter && (
                  <TableFooter>
                    <TableRow>
                      {columns?.map((column, colIndex) => (
                        <TableCell
                          className={classnames({
                            "status-column":
                              column?.dataKey === "status" && !colIndex,
                          })}
                          key={column.dataKey || colIndex}
                          align={column.footerAlign}
                          width={column.width}
                          sx={{
                            minWidth: column.minWidth,
                            padding: "0px !important",
                          }}
                        >
                          {typeof column?.footer === "string" ? (
                            <span
                              className={classnames({ "table-title": true })}
                            >
                              <Typography
                                variant={"body2"}
                                color="textTertiary"
                                sx={{ fontWeight: "bold" }}
                              >
                                {column?.footer}
                              </Typography>
                            </span>
                          ) : (
                            column.footer
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableFooter>
                )}
              </MuiTable>
            </TableContainer>
          </Box>
          {sortedData?.length === 0 &&
            (customNoData ? (
              customNoData
            ) : (
              <div className="no-data">
                <Typography style={{ fontWeight: "bold" }} variant="h6">
                  {noDataMessage}
                </Typography>
              </div>
            ))}
        </>
      )}
    </TableStyle>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      width: PropTypes.string.isRequired,
      footer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      align: PropTypes.oneOf(["inherit", "left", "center", "right", "justify"])
        .isRequired,
      render: PropTypes.func,
      footerAlign: PropTypes.oneOf([
        "inherit",
        "left",
        "center",
        "right",
        "justify",
      ]),
      sortable: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  noDataMessage: PropTypes.string,
  isExpandable: PropTypes.bool,
  onExpandRow: PropTypes.func,
  defaultExpandedRows: PropTypes.arrayOf(PropTypes.bool),
  onRowClick: PropTypes.func,
  customNoData: PropTypes.node,
  hasFooter: PropTypes.bool,
  stickyHeader: PropTypes.bool,
};

export default Table;
